import { useCallback, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { bindGameResync } from '../../../utils/bindGameResync';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const getToken = () => localStorage.getItem('auth_token');

export const useSpinWheelHostSession = ({ gameType }) => {
  const [host, setHost] = useState(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const hostRef = useRef(null);

  useEffect(() => {
    hostRef.current = host;
  }, [host]);

  const authHeader = { Authorization: `Bearer ${getToken()}` };

  const loadHost = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/host/session`, {
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success) {
        const h = data.host;
        if (!h || h.gameType === gameType) {
          setHost(h);
          setPayload(data.payload || null);
        } else {
          setHost(null);
          setPayload(null);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType]);

  useEffect(() => {
    loadHost();
  }, [loadHost]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ['websocket'] });
    socket.on('connect', () => {
      socket.emit('spinwheel:join-live');
      loadHost();
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) socket.emit('spinwheel:join-live');
      loadHost();
    });

    socket.on('spinwheel:host_updated', (p) => {
      const h = p?.host;
      if (!h) return;
      if (h.gameType !== gameType) return;
      setHost(h);
      setPayload(p);
    });

    socket.on('spinwheel:host_ended', (msg) => {
      const currentId = hostRef.current?.id;
      if (
        msg?.sessionId != null &&
        currentId != null &&
        Number(msg.sessionId) !== Number(currentId)
      ) {
        return;
      }
      if (hostRef.current?.gameType === gameType) {
        setHost(null);
        setPayload(null);
      }
    });

    return () => {
      unbindResync();
      socket.emit('spinwheel:leave-live');
      socket.disconnect();
    };
  }, [gameType, loadHost]);

  const post = async (path, body = {}) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/host${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Action failed');
      if (data.host) setHost(data.host);
      if (data.payload) setPayload(data.payload);
      return data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // Legacy actions (Users Wheel flow)
  const startSession = (body = {}) =>
    post('/session/start', { game_type: gameType, ...body });

  const gameAboutToStart = () =>
    post(`/session/${host.id}/game-about-to-start`);

  const force = () => post(`/session/${host.id}/force`);

  const selectHotSeatPlayer = (body) =>
    post(`/session/${host.id}/select-hot-seat`, body);

  const buildWheel = (body = {}) =>
    post(`/session/${host.id}/build-wheel`, body);

  const startWheel = () => post(`/session/${host.id}/start-wheel`);

  const endSession = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/spinning-wheel/admin/host/session/${host.id}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'End failed');
      setHost(null);
      setPayload(null);
      return data;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // Enhanced Hot Seat flow (BTN 1-7)
  const announce = (body = {}) =>
    post(`/session/${host.id}/announce`, body);

  const hotSeatSlide = () =>
    post(`/session/${host.id}/hot-seat-slide`);

  const luckyPlayer = () =>
    post(`/session/${host.id}/lucky-player`);

  const buildUserWheel = (body = {}) =>
    post(`/session/${host.id}/build-user-wheel`, body);

  const countdown = () =>
    post(`/session/${host.id}/countdown`);

  const spinUserWheel = () =>
    post(`/session/${host.id}/spin-user-wheel`);

  const startActualWheel = (body = {}) =>
    post(`/session/${host.id}/start-actual-wheel`, body);

  const selectHotSeatManual = (body = {}) =>
    post(`/session/${host.id}/select-hot-seat-manual`, body);

  return {
    host,
    payload,
    loading,
    busy,
    error,
    setError,
    loadHost,
    // Legacy
    startSession,
    gameAboutToStart,
    force,
    selectHotSeatPlayer,
    buildWheel,
    startWheel,
    endSession,
    // Enhanced Hot Seat (BTN 1-7)
    announce,
    hotSeatSlide,
    luckyPlayer,
    buildUserWheel,
    countdown,
    spinUserWheel,
    startActualWheel,
    selectHotSeatManual,
    phase: host?.phase || null,
  };
};

export default useSpinWheelHostSession;
